import type { FormEvent } from "react";
import { Alerta, Button, Row, Select, Stack, TextField } from "@/components";
import { campoNumero, formatM2, OPCIONES_TIPO, opcionesPiso, type ErroresUnidad, type FormUnidad } from "@/lib/unidades";
import type { TipoUnidad } from "@/lib/api";

export interface FormularioUnidadProps {
  form: FormUnidad;
  cambiar: <K extends keyof FormUnidad>(campo: K, valor: FormUnidad[K]) => void;
  errores: ErroresUnidad;
  /** Total de m² en vivo (null si alguna superficie es inválida). */
  total: string | null;
  onSubmit: (e: FormEvent) => void;
  onCancelar: () => void;
  guardando: boolean;
  error: string | null;
}

/** Campos de la unidad (alta, duplicar y edición). Sin `<Dialog>`: lo envuelven `UnidadesTab` y `UnidadTab`. */
export function FormularioUnidad({ form, cambiar, errores, total, onSubmit, onCancelar, guardando, error }: FormularioUnidadProps) {
  const numero = campoNumero(form.tipo);
  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack espacio={4}>
        <Row espacio={3} envolver>
          <Select etiqueta="Tipo" opciones={OPCIONES_TIPO} valor={form.tipo} onCambio={(v) => cambiar("tipo", v as TipoUnidad)} className="min-w-44" />
          <TextField etiqueta="Ambientes" inputMode="numeric" value={form.ambientes} onChange={(e) => cambiar("ambientes", e.target.value)} error={errores.ambientes} contenedorClassName="w-28" />
        </Row>
        <Row espacio={3} envolver>
          <Select etiqueta="Piso" opciones={opcionesPiso(form.piso)} valor={form.piso || undefined} onCambio={(v) => cambiar("piso", v)} error={errores.piso} className="min-w-44" />
          <TextField etiqueta={numero.etiqueta} placeholder={numero.placeholder} value={form.numero} onChange={(e) => cambiar("numero", e.target.value)} error={errores.numero} contenedorClassName="flex-1" />
        </Row>
        <Row espacio={3} envolver>
          <TextField etiqueta="Cubierta (m²)" inputMode="decimal" value={form.sup_cubierta_m2} onChange={(e) => cambiar("sup_cubierta_m2", e.target.value)} error={errores.sup_cubierta_m2} />
          <TextField etiqueta="Semicubierta (m²)" inputMode="decimal" value={form.sup_semicubierta_m2} onChange={(e) => cambiar("sup_semicubierta_m2", e.target.value)} error={errores.sup_semicubierta_m2} />
          <TextField etiqueta="Descubierta (m²)" inputMode="decimal" value={form.sup_descubierta_m2} onChange={(e) => cambiar("sup_descubierta_m2", e.target.value)} error={errores.sup_descubierta_m2} />
        </Row>
        <p className="text-chico text-texto-suave">{total === null ? "Total: —" : `Total: ${formatM2(total)}`}</p>
        <TextField etiqueta="Orientación" ayuda="Opcional" value={form.orientacion} onChange={(e) => cambiar("orientacion", e.target.value)} error={errores.orientacion} />
        <Row espacio={2} alinear="centro">
          <Button variante={form.fuera_de_venta ? "primario" : "secundario"} tamanio="sm" aria-pressed={form.fuera_de_venta} onClick={() => cambiar("fuera_de_venta", !form.fuera_de_venta)}>
            {form.fuera_de_venta ? "Fuera de venta" : "En venta"}
          </Button>
        </Row>
        {form.fuera_de_venta && (
          <TextField etiqueta="Motivo" value={form.motivo_fuera_de_venta} onChange={(e) => cambiar("motivo_fuera_de_venta", e.target.value)} error={errores.motivo_fuera_de_venta} />
        )}
        <TextField etiqueta="Notas" ayuda="Opcional" value={form.notas} onChange={(e) => cambiar("notas", e.target.value)} error={errores.notas} />
        <Alerta texto={error} />
        <Row justificar="fin" espacio={2}>
          <Button variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
